import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Section from '../common/Section';
import TestimonialCard from '../common/TestimonialCard';

const SectionTitle = styled(motion.h2)`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.space.md};
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  max-width: 600px;
  margin: 0 auto ${({ theme }) => theme.space.xl};
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: ${({ theme }) => theme.fontSizes.lg};
`;

const TestimonialsContainer = styled.div`
  position: relative;
  padding: ${({ theme }) => `0 ${theme.space.xl}`};
`;

const TestimonialsTrack = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  gap: ${({ theme }) => theme.space.xl};
  padding: ${({ theme }) => theme.space.md} 0;
  scroll-behavior: smooth;
  
  /* Hide scrollbar */
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: ${({ theme }) => theme.space.md};
  }
`;

const TestimonialItem = styled.div`
  flex: 0 0 calc(33.333% - ${({ theme }) => theme.space.lg});
  scroll-snap-align: start;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.xl}) {
    flex: 0 0 calc(50% - ${({ theme }) => theme.space.lg});
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex: 0 0 calc(100% - ${({ theme }) => theme.space.md});
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.grey[200]};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: ${({ theme }) => theme.transitions.normal};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`;

const PrevButton = styled(NavigationButton)`
  left: 0;
`;

const NextButton = styled(NavigationButton)`
  right: 0;
`;

const testimonials = [
  {
    quote: "DataDriven completely transformed our marketing approach. Their AI-powered analytics gave us insights we never knew were possible, leading to a 43% increase in conversion rates.",
    name: "Sarah Johnson",
    position: "CMO",
    company: "TechSolutions Inc.",
    imageSrc: "https://randomuser.me/api/portraits/women/1.jpg"
  },
  {
    quote: "The business intelligence dashboards they created for us have become an essential part of our decision-making process. We now have real-time visibility into all our key metrics.",
    name: "Michael Chen",
    position: "CEO",
    company: "Innovate Partners",
    imageSrc: "https://randomuser.me/api/portraits/men/2.jpg"
  },
  {
    quote: "Their strategic consulting helped us identify gaps in our customer journey that we were completely unaware of. Since implementing their recommendations, our customer retention has increased by 28%.",
    name: "Emily Rodriguez",
    position: "Marketing Director",
    company: "Global Brands Ltd.",
    imageSrc: "https://randomuser.me/api/portraits/women/3.jpg"
  },
  {
    quote: "Working with DataDriven has been a game-changer for our digital marketing efforts. Their data-first approach has helped us optimize our ad spend and maximize ROI.",
    name: "David Wilson",
    position: "Digital Marketing Manager",
    company: "RetailNow",
    imageSrc: "https://randomuser.me/api/portraits/men/4.jpg"
  },
  {
    quote: "The team at DataDriven are true experts in their field. They took the time to understand our unique business challenges and delivered tailored solutions that drove real results.",
    name: "Jennifer Lee",
    position: "Operations Director",
    company: "HealthTech Solutions",
    imageSrc: "https://randomuser.me/api/portraits/women/5.jpg"
  }
];

const TestimonialsSection: React.FC = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  
  const scrollPrev = () => {
    if (trackRef.current) {
      const width = trackRef.current.clientWidth;
      trackRef.current.scrollLeft -= width / 2;
    }
  };
  
  const scrollNext = () => {
    if (trackRef.current) {
      const width = trackRef.current.clientWidth;
      trackRef.current.scrollLeft += width / 2;
    }
  };
  
  return (
    <Section id="testimonials">
      <SectionTitle
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.5 }}
      >
        What Our Clients Say
      </SectionTitle>
      
      <SectionSubtitle
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        Don't just take our word for it — hear from some of our satisfied clients
      </SectionSubtitle>
      
      <TestimonialsContainer>
        <PrevButton onClick={scrollPrev}>
          <FaChevronLeft />
        </PrevButton>
        
        <TestimonialsTrack ref={trackRef}>
          {testimonials.map((testimonial, index) => (
            <TestimonialItem key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
              >
                <TestimonialCard
                  quote={testimonial.quote}
                  name={testimonial.name}
                  position={testimonial.position}
                  company={testimonial.company}
                  imageSrc={testimonial.imageSrc}
                />
              </motion.div>
            </TestimonialItem>
          ))}
        </TestimonialsTrack>
        
        <NextButton onClick={scrollNext}>
          <FaChevronRight />
        </NextButton>
      </TestimonialsContainer>
    </Section>
  );
};

export default TestimonialsSection;
